/**
 * Browser Web Push helper.
 *
 * Encapsulates the permission + subscription dance:
 * 1. Check that the browser supports Notifications, Service Workers, and PushManager.
 * 2. Request `Notification.permission` if it is still `'default'`.
 * 3. Get the active service worker registration.
 * 4. Re-use an existing PushSubscription if present, otherwise call
 *    `pushManager.subscribe({ userVisibleOnly: true, applicationServerKey })`
 *    using the VAPID public key fetched from the web-backend.
 * 5. Register the resulting subscription with the backend so the scheduler
 *    worker can deliver "notify on complete" notifications.
 */

import {
  getVapidPublicKey,
  registerPushSubscription,
  unregisterPushSubscription,
} from '@/api/pushSubscriptions';

export type EnsureSubscriptionReason =
  | 'unsupported'
  | 'denied'
  | 'no-vapid-key'
  | 'sw-unavailable'
  | 'sw-timeout'
  | 'subscribe-failed'
  | 'register-failed'
  | 'unknown';

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => {
      reject(new Error(`${label} timed out after ${String(ms)}ms`));
    }, ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    );
  });
}

export interface EnsureSubscriptionResult {
  ok: boolean;
  reason?: EnsureSubscriptionReason;
}

/** Convert a base64url-encoded VAPID public key to the ArrayBuffer PushManager wants. */
function urlBase64ToBuffer(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new ArrayBuffer(raw.length);
  const view = new Uint8Array(out);
  for (let i = 0; i < raw.length; i += 1) {
    view[i] = raw.charCodeAt(i);
  }
  return out;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let bin = '';
  for (let i = 0; i < bytes.byteLength; i += 1) bin += String.fromCharCode(bytes[i] ?? 0);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type PushLog = (step: string, extra?: unknown) => void;

/**
 * Resolve a usable ServiceWorkerRegistration for push subscription.
 *
 * Tries, in order:
 *   1. `navigator.serviceWorker.ready` with a short budget (fast path on
 *      pages where the SW is already active).
 *   2. `getRegistration()` for whatever the browser has, even if it's still
 *      installing or waiting — pushManager works on those.
 *   3. If the registration exists but has no active worker yet, wait up to
 *      a longer budget for the installing/waiting worker to transition to
 *      'activated' via statechange events.
 *
 * Returns `null` only when there's genuinely no SW we can talk to.
 */
async function acquireServiceWorkerRegistration(
  log: PushLog
): Promise<ServiceWorkerRegistration | null> {
  // 1) Fast path — already active.
  try {
    log('sw: trying ready (fast path)');
    const ready = await withTimeout(navigator.serviceWorker.ready, 3000, 'serviceWorker.ready');
    log('sw: ready resolved (fast path)');
    return ready;
  } catch (e) {
    log('sw: ready fast-path miss, falling back', e);
  }

  // 2) Use any existing registration, regardless of worker state.
  let registration: ServiceWorkerRegistration | null = null;
  try {
    registration = (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch (e) {
    log('sw: getRegistration threw', e);
  }
  if (!registration) {
    log('sw: no registration found');
    return null;
  }
  log('sw: registration found', {
    hasActive: !!registration.active,
    hasWaiting: !!registration.waiting,
    hasInstalling: !!registration.installing,
  });

  // If something is waiting, nudge it to activate (skipWaiting message —
  // the generated SW already calls skipWaiting itself, but this covers the
  // case of a stale SW from an older deploy before that config landed).
  if (registration.waiting) {
    try {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } catch {
      /* best-effort */
    }
  }

  // 3) Any worker — installing, waiting, or active — is enough for
  //    pushManager.subscribe to function. Return it immediately.
  if (registration.active || registration.installing || registration.waiting) {
    return registration;
  }

  // No worker at all. Wait briefly for one to appear via updatefound.
  const reg = registration;
  log('sw: no worker yet, waiting for updatefound');
  return await new Promise<ServiceWorkerRegistration | null>((resolve) => {
    const t = setTimeout(() => {
      log('sw: updatefound wait timed out');
      resolve(null);
    }, 15000);
    reg.addEventListener('updatefound', () => {
      clearTimeout(t);
      log('sw: updatefound fired');
      resolve(reg);
    });
  });
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
}

/**
 * Ensure the current browser has a registered Web Push subscription.
 *
 * Safe to call repeatedly — re-registering an existing subscription with the
 * backend just refreshes the row. Returns `{ ok: false, reason }` for any
 * non-success outcome so callers (e.g. the task form toggle) can revert UI
 * state and surface a helpful message.
 */
export async function ensurePushSubscription(): Promise<EnsureSubscriptionResult> {
  const log = (step: string, extra?: unknown): void => {
    if (extra !== undefined) {
      console.info(`[push] ${step}`, extra);
    } else {
      console.info(`[push] ${step}`);
    }
  };

  if (!isPushSupported()) {
    log('unsupported');
    return { ok: false, reason: 'unsupported' };
  }

  let permission = Notification.permission;
  log('initial permission', permission);
  if (permission === 'default') {
    permission = await Notification.requestPermission();
    log('permission after prompt', permission);
  }
  if (permission !== 'granted') {
    return { ok: false, reason: 'denied' };
  }

  // Acquire a service-worker registration we can call pushManager on.
  //
  // Earlier this code blocked on `navigator.serviceWorker.ready` with a 5s
  // timeout and required `registration.active` to be set, then bailed with
  // sw-timeout otherwise. That misfired in two ways:
  //
  // 1) `.ready` resolves only when an *active* SW exists for the page's
  //    scope. After a fresh deploy the new SW spends a few seconds in
  //    "installing" → "activating" (precaching all bundles). On a slow
  //    network the 5s budget runs out before the SW finishes activating,
  //    and we'd report sw-timeout to a user whose SW was perfectly healthy.
  // 2) `pushManager.subscribe()` and `getSubscription()` work fine on a
  //    registration whose worker is still installing or waiting — we never
  //    needed the `.active` precondition.
  //
  // The implementation below first tries the fast path (.ready, short
  // budget), then falls back to using whatever registration the browser
  // already has, and if needed waits for an installing/waiting SW to reach
  // 'activated' via statechange events before giving up.
  const registration = await acquireServiceWorkerRegistration(log);
  if (!registration) {
    return { ok: false, reason: 'sw-timeout' };
  }

  let subscription: PushSubscription | null = null;
  try {
    log('getting existing subscription');
    subscription = await withTimeout(
      registration.pushManager.getSubscription(),
      5000,
      'pushManager.getSubscription'
    );
    log('existing subscription', subscription ? 'found' : 'none');
  } catch (e) {
    log('getSubscription failed', e);
    return { ok: false, reason: 'subscribe-failed' };
  }

  if (!subscription) {
    let vapidKey: string;
    try {
      log('fetching VAPID public key');
      const resp = await getVapidPublicKey();
      vapidKey = resp.public_key;
      log('VAPID key fetched', { length: vapidKey.length });
    } catch (e) {
      log('VAPID fetch failed', e);
      return { ok: false, reason: 'no-vapid-key' };
    }
    if (!vapidKey) {
      return { ok: false, reason: 'no-vapid-key' };
    }
    try {
      log('subscribing to pushManager');
      subscription = await withTimeout(
        registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToBuffer(vapidKey),
        }),
        15000,
        'pushManager.subscribe'
      );
      log('pushManager.subscribe succeeded');
    } catch (e) {
      log('pushManager.subscribe failed', e);
      return { ok: false, reason: 'subscribe-failed' };
    }
  }

  const json = subscription.toJSON();
  const endpoint = json.endpoint ?? subscription.endpoint;
  const p256dh = json.keys?.p256dh ?? arrayBufferToBase64Url(subscription.getKey('p256dh'));
  const auth = json.keys?.auth ?? arrayBufferToBase64Url(subscription.getKey('auth'));
  if (!endpoint || !p256dh || !auth) {
    log('subscription missing keys', {
      hasEndpoint: !!endpoint,
      hasP256dh: !!p256dh,
      hasAuth: !!auth,
    });
    return { ok: false, reason: 'subscribe-failed' };
  }

  try {
    log('registering subscription with backend');
    await registerPushSubscription({
      endpoint,
      keys: { p256dh, auth },
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    });
    log('backend registration succeeded');
  } catch (e) {
    log('backend registration failed', e);
    return { ok: false, reason: 'register-failed' };
  }

  return { ok: true };
}

/**
 * Remove the current browser's subscription from the backend and from the
 * browser's PushManager. Not currently called from the task form (we keep
 * the subscription alive between tasks) but exposed for settings UIs.
 */
export async function clearPushSubscription(): Promise<void> {
  if (!isPushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      if (endpoint) {
        try {
          await unregisterPushSubscription(endpoint);
        } catch (err) {
          // Best-effort cleanup — server may already have forgotten the
          // subscription. Surface for DevTools but don't propagate.
          console.warn('Failed to unregister push subscription on server', err);
        }
      }
    }
  } catch (err) {
    console.warn('Failed to clear push subscription', err);
  }
}
